/**
 * AP-7 — schlanke Listendarstellung fuer die Views A.
 *
 * Bewusst ein <table> und nicht die CanvasTable aus AP-5: diese Listen sind
 * einige hundert Zeilen gross, nicht 100.000, und brauchen dafuer weder
 * Virtual Scrolling noch Fade-Farben. Auswahl ist wie im Original
 * Einfachauswahl (LVS_SINGLESEL).
 */

import './views.css';

export interface DataTableOptions {
  columns: readonly string[];
  /** Meldet die Zeilennummer der Auswahl; ohne Angabe ist die Liste nicht auswaehlbar. */
  onSelect?: (row: number) => void;
  emptyText?: string;
}

export interface DataTableHandle {
  /** Scrollrahmen — direkt in den Views-Baum haengen. */
  element: HTMLElement;
  setRows(rows: readonly (readonly string[])[]): void;
  /** Setzt eine einzelne Zelle, ohne die Tabelle neu aufzubauen. */
  setCell(row: number, column: number, text: string): void;
  select(row: number): void;
  readonly selectedRow: number;
  clear(): void;
}

export function createDataTable(options: DataTableOptions): DataTableHandle {
  const element = document.createElement('div');
  element.className = 'cm-table-scroll';

  const table = document.createElement('table');
  table.className = 'cm-data-table';

  const head = document.createElement('thead');
  const headRow = document.createElement('tr');
  for (const column of options.columns) {
    const th = document.createElement('th');
    th.textContent = column;
    headRow.append(th);
  }
  head.append(headRow);

  const body = document.createElement('tbody');
  table.append(head, body);
  element.append(table);

  let selectedRow = -1;

  function applySelection(next: number): void {
    const rows = body.rows;
    if (selectedRow >= 0 && selectedRow < rows.length) {
      rows[selectedRow]?.setAttribute('aria-selected', 'false');
    }
    selectedRow = next;
    if (next >= 0 && next < rows.length) {
      const row = rows[next];
      row?.setAttribute('aria-selected', 'true');
      row?.scrollIntoView({ block: 'nearest' });
    }
  }

  function setRows(rows: readonly (readonly string[])[]): void {
    body.replaceChildren();
    selectedRow = -1;
    if (rows.length === 0) {
      const tr = document.createElement('tr');
      const td = document.createElement('td');
      td.colSpan = options.columns.length;
      td.className = 'cm-data-table__empty';
      td.textContent = options.emptyText ?? '';
      tr.append(td);
      body.append(tr);
      return;
    }
    const selectable = options.onSelect !== undefined;
    rows.forEach((cells, index) => {
      const tr = document.createElement('tr');
      tr.setAttribute('aria-selected', 'false');
      if (selectable) {
        tr.dataset['selectable'] = '1';
        tr.addEventListener('pointerdown', () => {
          applySelection(index);
          options.onSelect?.(index);
        });
      }
      for (let col = 0; col < options.columns.length; col += 1) {
        const td = document.createElement('td');
        td.textContent = cells[col] ?? '';
        tr.append(td);
      }
      body.append(tr);
    });
  }

  return {
    element,
    setRows,
    setCell(row, column, text) {
      const cell = body.rows[row]?.cells[column];
      if (cell !== undefined) cell.textContent = text;
    },
    select: applySelection,
    get selectedRow() {
      return selectedRow;
    },
    clear() {
      setRows([]);
    },
  };
}
